/**
 * The resident record, read-only (PATIENT_RECORD_SEARCH.md section 3).
 *
 * Header, "Who owns it", then only the board sections the server sent. A board whose section is absent shows
 * its owner and stage word in the owner panel and nothing else: no empty card, no placeholder that implies
 * there is something to see. There is no edit control anywhere in here; each board's own screen is the only
 * place to change anything, and the links go there.
 *
 * Hand-built like the send-back dialog (this project's Jest cannot load the kit's Radix Dialog): a labelled
 * modal dialog, focus moved in on open and back to the row on close, Tab held inside, Escape closes it.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { StateBlock } from '../tide';
import {
  residentDirectoryApi,
  type DirectoryRecord, type FinanceSection, type PaSection, type UmedSection,
} from './residentDirectoryApi';
import {
  NO_BUILDING, NO_NAME, financeOwnerWords, fmtDay, fmtStamp, money, paOwnerWords, phrase, providerWords, umedOwnerWords,
} from './residentWords';
import './residentDirectory.css';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Fact({ k, v }: { k: string; v: React.ReactNode }) {
  if (v === null || v === undefined || v === '' || v === false) return null;
  return (<><dt>{k}</dt><dd>{v}</dd></>);
}

function PaCard({ s }: { s: PaSection }) {
  return (
    <section className="rd-card" aria-label="PA case">
      <h4>PA case</h4>
      <dl className="rd-facts">
        <Fact k="Reference" v={s.reference} />
        <Fact k="Case" v={s.open ? 'Open' : 'Closed'} />
        <Fact k="Stage" v={phrase(s.stage)} />
        <Fact k="Status" v={phrase(s.status_word)} />
        <Fact k="Disposition" v={phrase(s.disposition)} />
        <Fact k="Held by" v={s.holder} />
        <Fact k="Opened" v={fmtDay(s.opened_on)} />
        <Fact k="Last contact" v={fmtDay(s.last_contact_on)} />
        <Fact k="Closed" v={fmtDay(s.closed_on)} />
        <Fact k="Closed because" v={phrase(s.closed_reason)} />
      </dl>
    </section>
  );
}

function UmedCard({ s }: { s: UmedSection }) {
  return (
    <section className="rd-card" aria-label="UMED case">
      <h4>UMED case</h4>
      <dl className="rd-facts">
        <Fact k="Reference" v={s.reference} />
        <Fact k="Stage" v={phrase(s.stage)} />
        <Fact k="Status" v={phrase(s.status_word)} />
        <Fact k="Operator" v={s.operator} />
        <Fact k="Exception" v={s.exception ? `${phrase(s.exception)}${s.exception_state ? ` (${phrase(s.exception_state)})` : ''}` : null} />
        <Fact k="Submitted" v={fmtDay(s.submitted_on)} />
        <Fact k="Approved" v={fmtDay(s.approved_on)} />
        <Fact k="NOCA" v={phrase(s.approval?.noca)} />
        <Fact k="FLMMIS" v={phrase(s.approval?.flmmis)} />
        <Fact k="Approval proven" v={s.approval ? (s.approval.proven ? 'Yes' : 'No') : null} />
        <Fact k="Correction" v={s.correction ? `${phrase(s.correction.type) || 'Correction'}: ${phrase(s.correction.state)}` : null} />
        <Fact k="Reversal" v={s.reversal ? phrase(s.reversal.state) : null} />
        <Fact k="Finance" v={phrase(s.finance_state)} />
      </dl>
    </section>
  );
}

function FinanceCard({ s }: { s: FinanceSection }) {
  const t = s.terms;
  return (
    <section className="rd-card" aria-label="Finance">
      <h4>Finance</h4>
      <dl className="rd-facts">
        <Fact k="Handoff" v={s.handed_not_received ? 'Handed, not received' : phrase(s.state)} />
        <Fact k="Accepted by" v={s.accepted_by} />
        <Fact k="Accepted" v={fmtStamp(s.accepted_at)} />
        <Fact k="Payment" v={phrase(s.payment_state)} />
        <Fact k="Term" v={t && t.term_months !== null ? `${t.term_months} ${t.term_months === 1 ? 'month' : 'months'}` : null} />
        <Fact k="Monthly" v={t ? money(t.monthly_cents) : null} />
        <Fact k="Total" v={t ? money(t.total_cents) : null} />
        <Fact k="Resubmittal" v={s.resubmittal ? `${phrase(s.resubmittal.kind) || 'Resubmittal'}: ${phrase(s.resubmittal.state)}` : null} />
        <Fact k="Closed" v={fmtStamp(s.closed_at)} />
        <Fact k="Closed because" v={phrase(s.close_reason)} />
      </dl>
    </section>
  );
}

function Owners({ r }: { r: DirectoryRecord }) {
  const stage = (w: string | null) => (w ? <span className="rd-stage">{phrase(w)}</span> : null);
  return (
    <section className="rd-card" aria-label="Who owns it">
      <h4>Who owns it</h4>
      <dl className="rd-facts">
        <dt>PA</dt><dd>{paOwnerWords(r.owners.pa)}{stage(r.stages.pa)}</dd>
        {r.owners.pa_facility ? (<><dt>Facility PA</dt><dd>{r.owners.pa_facility}</dd></>) : null}
        <dt>UMED</dt><dd>{umedOwnerWords(r.owners)}{stage(r.stages.umed)}</dd>
        <dt>Finance</dt><dd>{financeOwnerWords(r.owners.finance)}{stage(r.stages.finance)}</dd>
        <dt>Providers</dt><dd>{providerWords(r.owners)}</dd>
      </dl>
    </section>
  );
}

const LINK_LABEL: Record<'pa' | 'umed' | 'finance', string> = {
  pa: 'Open in PA workspace', umed: 'Open in UMED', finance: 'Open in Finance',
};

function BoardLink({ to, children, onGo }: { to: string; children: React.ReactNode; onGo: () => void }) {
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link to={to} className="rd-link" onClick={onGo}>{children}</Link>
    : <a href={to} className="rd-link">{children}</a>;
}

export interface ResidentRecordDrawerProps {
  patientId: string;
  /** The row that opened the drawer: focus goes back to it on close. */
  returnFocusTo?: HTMLElement | null;
  onClose: () => void;
  /** A board link was followed: the caller closes everything (default: just this drawer). */
  onNavigate?: () => void;
}

export default function ResidentRecordDrawer({ patientId, returnFocusTo, onClose, onNavigate }: ResidentRecordDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const returnRef = useRef(returnFocusTo);
  returnRef.current = returnFocusTo;
  const titleId = useId();
  const [rec, setRec] = useState<DirectoryRecord | null>(null);
  const [fail, setFail] = useState<{ message: string; status?: number } | null>(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closeRef.current(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); panelRef.current.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === panelRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = returnRef.current && returnRef.current.isConnected ? returnRef.current : before;
      back?.focus?.();
    };
  }, []);

  useEffect(() => {
    let live = true;
    setRec(null);
    setFail(null);
    residentDirectoryApi.record(patientId).then((r) => {
      if (!live) return;
      if (r.ok && r.data) setRec(r.data); else setFail({ message: r.message || 'This record could not be opened.', status: r.status });
    });
    return () => { live = false; };
  }, [patientId, tries]);

  const retry = useCallback(() => setTries((n) => n + 1), []);

  const h = rec?.header;
  const where = h ? `${h.building?.name || NO_BUILDING}${h.clinic?.name ? ` · ${h.clinic.name}` : ''}` : '';
  const born = h ? (h.dob ? `Born ${fmtDay(h.dob)}` : h.birth_year ? `Born ${h.birth_year}` : '') : '';

  return (
    <div className="rd-drawer-root">
      <div className="rd-backdrop" onClick={() => closeRef.current()} aria-hidden="true" />
      <div className="rd-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={panelRef}>
        <div className="rd-drawer-top">
          <h2 id={titleId} className="rd-title">Resident record</h2>
          <button type="button" className="rd-ghost" onClick={() => closeRef.current()}>Close</button>
        </div>

        {!rec && !fail && <StateBlock kind="loading" shape="lines" label="Opening the record" />}
        {fail && (
          <StateBlock
            kind="error"
            message={fail.message}
            status={fail.status}
            onRetry={fail.status === 404 || fail.status === 403 ? undefined : retry}
          />
        )}

        {rec && h && (
          <div className="rd-record">
            <header className="rd-head">
              <h3>{h.name ? h.name : <em className="rd-noname">{NO_NAME}</em>}</h3>
              <p className="rd-meta-line">
                {[born, h.counselear_id ? `CounselEAR ${h.counselear_id}` : '', where, phrase(h.resident_status)].filter(Boolean).join(' · ')}
              </p>
              {rec.merged_from ? <p className="rd-meta-line">Opened from a record that was merged into this one.</p> : null}
            </header>

            <Owners r={rec} />

            {rec.sections.pa && <PaCard s={rec.sections.pa} />}
            {rec.sections.umed && <UmedCard s={rec.sections.umed} />}
            {rec.sections.finance && <FinanceCard s={rec.sections.finance} />}

            {(rec.links.pa || rec.links.umed || rec.links.finance) && (
              <nav className="rd-links" aria-label="Open in a board">
                <p className="rd-meta-line">This view is read-only. To change anything, open the board.</p>
                {(['pa', 'umed', 'finance'] as const).map((k) => {
                  const to = rec.links[k];
                  return to ? <BoardLink key={k} to={to} onGo={() => (onNavigate ? onNavigate() : closeRef.current())}>{LINK_LABEL[k]}</BoardLink> : null;
                })}
              </nav>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
